import { Button, Card, CardActions, CardContent, CardHeader, Menu, MenuItem, Typography } from '@mui/material';
import React, { useRef } from 'react';
import { useDrop } from 'react-dnd';
import { useTranslation } from 'react-i18next';
import { useBoolean } from '../../../hooks/useBoolean';
import { useContextMenu } from '../../../hooks/useContextMenu';
import {
    CreateFormQuestionOptionBody,
    MoveFormQuestionOptionBody,
    UpdateFormQuestionOptionBody,
} from '../../../services/apiSLTP/qhse/forms/formQuestionOptions';
import { UpdateFormQuestionBody } from '../../../services/apiSLTP/qhse/forms/formQuestions';
import { FormSectionWithQuestions, UpdateFormSectionBody } from '../../../services/apiSLTP/qhse/forms/formSections';
import { capitalizeFirstLetter } from '../../../utils/capitalize';
import MoveSectionsModal from '../../modals/qhse/forms/MoveSectionsModal';
import CustomTextInput from '../../ui/CustomTextInput';
import Icon from '../../ui/Icon';
import IconButton from '../../ui/IconButton';
import AddQuestionButtons from '../questions/AddQuestionButtons';
import QuestionItem from '../questions/QuestionItem';

interface SectionItemProps {
    section: FormSectionWithQuestions;
    sectionIndex: number;
    moveQuestion: (fromSectionIndex: number, fromIndex: number, toSectionIndex: number, toIndex: number) => void;
    selectedId: string | null;
    setSelectedId: (id: string | null) => void;
    sectionsLength: number;
    sections: FormSectionWithQuestions[];
    formColor: string;
    globalOffset: (questionId: string) => number;
    onDrag(isDragging: boolean, itemId: string | null): void;
    isDragging: boolean;
    draggingItemId: string | null;
    onAddQuestion: (sectionId: string, type: number, order: number, setSelectedId: (id: string | null) => void) => void;
    onAddSection: (templateId: string, order: number, setSelectedId: (id: string | null) => void) => void;
    onMoveSection: (sectionId: string, order: number) => void;
    onMoveQuestion: (questionId: string, toSectionId: string, order: number) => void;
    onUpdateQuestion: (questionId: string, data: UpdateFormQuestionBody) => void;
    onUpdateSection: (sectionId: string, data: UpdateFormSectionBody) => void;
    onDuplicateQuestion: (questionId: string, setSelectedId: (id: string | null) => void) => void;
    onDeleteQuestion: (questionId: string) => void;
    onDuplicateSection: (sectionId: string, setSelectedId: (id: string | null) => void) => void;
    onDeleteSection: (sectionId: string, withContent: boolean) => void;
    onAddQuestionOption: (data: CreateFormQuestionOptionBody) => void;
    onDeleteQuestionOption: (optionId: string) => void;
    onUpdateQuestionOption: (optionId: string, data: UpdateFormQuestionOptionBody) => void;
    onMoveQuestionOption: (optionId: string, data: MoveFormQuestionOptionBody) => void;
    disabled?: boolean;
}

const SectionItem: React.FC<SectionItemProps> = ({
    section,
    sectionIndex,
    moveQuestion,
    selectedId,
    setSelectedId,
    sectionsLength,
    sections,
    onAddQuestion,
    formColor,
    globalOffset,
    onDrag,
    isDragging,
    draggingItemId,
    onMoveSection,
    onAddSection,
    onMoveQuestion,
    onUpdateQuestion,
    onUpdateSection,
    onDuplicateQuestion,
    onDeleteQuestion,
    onDuplicateSection,
    onDeleteSection,
    onAddQuestionOption,
    onDeleteQuestionOption,
    onUpdateQuestionOption,
    onMoveQuestionOption,
    disabled = false,
}) => {
    const isSelected = selectedId === section._id;
    const { t } = useTranslation();
    const ref = useRef<HTMLDivElement>(null);
    const isSelectedInSection = isSelected || section.questions.some((q) => q._id === selectedId);
    const [title, setTitle] = React.useState(section.title || '');
    const { anchorPosition, isOpen, handleOpen, handleClose } = useContextMenu(false);
    const moveSectionModal = useBoolean();

    const [, drop] = useDrop({
        accept: 'QUESTION',
        hover: (item: { index: number; sectionIndex: number; id: string }, monitor) => {
            if (!ref.current) return;

            if (section.questions.length === 0 && item.sectionIndex !== sectionIndex) {
                moveQuestion(item.sectionIndex, item.index, sectionIndex, 0);
                item.index = 0;
                item.sectionIndex = sectionIndex;
            }
        },
        collect: (monitor) => ({ isOver: monitor.isOver({ shallow: true }) }),
        drop: (item, monitor) => {
            onDrag(false, null);
            const hoverIndex = section.questions.findIndex((q) => q._id === item.id);
            const targetIndex = hoverIndex >= 0 ? hoverIndex : item.index;
            onMoveQuestion(item.id, section._id, targetIndex);
        },
    });

    drop(ref);

    return (
        <Card ref={ref} sx={{ width: '100%', borderRadius: 0 }}>
            <CardHeader
                sx={{ bgcolor: 'action.selected' }}
                title={
                    <Typography variant="subtitle1" component="div">
                        {`${capitalizeFirstLetter(t('section'))} ${sectionIndex + 1}`}
                    </Typography>
                }
                action={
                    <>
                        {!disabled && <IconButton iconName="moreVert" onClick={handleOpen} />}
                        <Menu open={isOpen} onClose={handleClose} anchorReference="anchorPosition" anchorPosition={anchorPosition || undefined}>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    onDuplicateSection(section._id, setSelectedId);
                                }}
                            >
                                {capitalizeFirstLetter(t('duplicateSection'))}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    onDeleteSection(section._id, false);
                                }}
                                disabled={sectionsLength <= 1}
                            >
                                {capitalizeFirstLetter(t('deleteSectionOnly'))}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    onDeleteSection(section._id, true);
                                }}
                                disabled={sectionsLength <= 1}
                            >
                                {capitalizeFirstLetter(t('deleteSectionWithQuestions'))}
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    handleClose();
                                    moveSectionModal.setTrue();
                                }}
                            >
                                {capitalizeFirstLetter(t('moveSection'))}
                            </MenuItem>
                        </Menu>
                    </>
                }
            />
            <CardContent
                sx={{
                    p: 1,
                    gap: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    borderLeft: isSelected ? `6px solid ${formColor}` : '6px solid transparent',
                }}
            >
                <Card
                    sx={{
                        borderLeft: '6px solid transparent',
                        cursor: 'pointer',
                        bgcolor: isSelected ? 'action.selected' : 'transparent',
                        p: 1,
                        borderRadius: 0,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                            bgcolor: isSelected ? 'action.selected' : 'action. hover',
                        },
                    }}
                    onClick={() => setSelectedId(section._id)}
                    elevation={0}
                >
                    {isSelected ? (
                        <CustomTextInput
                            value={title}
                            onChange={(text) => {
                                setTitle(text);
                                onUpdateSection(section._id, { title: text });
                            }}
                            autoFocus
                        />
                    ) : (
                        <Typography variant="h5" component="div" sx={{ minHeight: '25px' }}>
                            {title}
                        </Typography>
                    )}
                </Card>
                {!disabled && isSelected && (
                    <AddQuestionButtons
                        sectionId={section._id}
                        index={section.questions.length}
                        onAddQuestion={onAddQuestion}
                        setSelectedId={setSelectedId}
                    />
                )}
                {section.questions.map((question, qIndex) => (
                    <QuestionItem
                        key={question._id}
                        question={question}
                        sectionIndex={sectionIndex}
                        sectionsLength={sectionsLength}
                        questionsLength={section.questions.length}
                        index={qIndex}
                        moveQuestion={moveQuestion}
                        selectedId={selectedId}
                        setSelectedId={setSelectedId}
                        formColor={formColor}
                        number={globalOffset(question._id)}
                        onDrag={onDrag}
                        isGlobalDragging={isDragging}
                        draggingItemId={draggingItemId}
                        onAddQuestion={onAddQuestion}
                        sectionId={section._id}
                        onUpdateQuestion={onUpdateQuestion}
                        onDuplicateQuestion={onDuplicateQuestion}
                        onDeleteQuestion={onDeleteQuestion}
                        onAddQuestionOption={onAddQuestionOption}
                        onDeleteQuestionOption={onDeleteQuestionOption}
                        onUpdateQuestionOption={onUpdateQuestionOption}
                        onMoveQuestionOption={onMoveQuestionOption}
                        onMoveQuestion={onMoveQuestion}
                        disabled={disabled}
                    />
                ))}
            </CardContent>
            {!disabled && (isSelectedInSection || (selectedId === null && sectionsLength === sectionIndex + 1)) && (
                <CardActions>
                    <Button
                        startIcon={<Icon name="addCircle" />}
                        color="info"
                        onClick={() => onAddSection(section.templateId, sectionIndex + 1, setSelectedId)}
                    >
                        {capitalizeFirstLetter(t('addNewSection'))}
                    </Button>
                </CardActions>
            )}
            {moveSectionModal.value && (
                <MoveSectionsModal
                    onMoveSection={onMoveSection}
                    handleClose={moveSectionModal.setFalse}
                    open={moveSectionModal.value}
                    sectionId={section._id}
                    templateId={section.templateId}
                />
            )}
        </Card>
    );
};

export default SectionItem;
